import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@ntconsult/aluma-react';
import { useCallback, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  type AllocationFormValues,
  allocationFormSchema,
  type EditAllocationTarget,
} from '../components/AllocationForm.schema';
import type { Allocation, AllocationBody } from '../types/Allocation';
import type { Warning } from '../types/ApiError';
import type { ProjectOption } from '../types/Project';
import type { SalesProfileOption } from '../types/SalesProfile';
import type { TagOption } from '../types/Tag';
import {
  formFieldFor,
  initialValues,
  noticeBody,
  projectBills,
  valuesBody,
} from '../utils/allocationForm';
import { toApiDate } from '../utils/data';
import { i18nKeyForError, parseApiError } from '../utils/errors';
import { DEFAULT_SOLD_HOURS, decimalInputFormat } from '../utils/money';
import { noticeEndDate } from '../utils/poolNotice';
import { useAllocationDefaults } from './useAllocationDefaults';
import type { SelectedOption } from './useAllocationFilters';
import { useContracts } from './useContracts';
import { useProjects } from './useProjects';
import { useSalesProfiles } from './useSalesProfiles';
import { useSaveAllocation } from './useSaveAllocation';
import { useValidateAllocation } from './useValidateAllocation';

function useAllocatableContracts(search: string) {
  return useContracts(search, { allocatable: true });
}

export type UseAllocationFormParams = {
  target: EditAllocationTarget | null;
  today: string;
  readOnly: boolean;
  onSaveSuccess: () => void;
};

export function useAllocationForm({
  target,
  today,
  readOnly,
  onSaveSuccess,
}: UseAllocationFormParams) {
  const { t, i18n } = useTranslation(['alocacao', 'common']);
  const { toast } = useToast();
  const saveMutation = useSaveAllocation();
  const fetchDefaults = useAllocationDefaults();

  const [submitWarnings, setSubmitWarnings] = useState<Warning[]>([]);
  // Objetos completos, não só ids: uma tag já selecionada pode não estar na
  // busca atual do combobox e precisa do nome/cor guardados pra exibir o chip.
  const [selectedTags, setSelectedTags] = useState<TagOption[]>(
    () => target?.allocation.tags ?? [],
  );

  const {
    control,
    register,
    handleSubmit,
    getValues,
    setValue,
    setError,
    formState: { errors, isValid, isDirty },
  } = useForm<AllocationFormValues>({
    resolver: zodResolver(allocationFormSchema),
    mode: 'onChange',
    defaultValues: initialValues(target, today),
  });

  const values = useWatch({ control });

  const selectedContractId = values.contract?.id;
  const selectedProject = values.project as ProjectOption | undefined;
  const billsProject = projectBills(selectedProject);
  const isPool = !!selectedProject?.isPool;

  const { data: salesProfilesPage } = useSalesProfiles(selectedContractId);
  const salesProfiles = salesProfilesPage?.items ?? [];
  const selectedProfile = salesProfiles.find((profile) => profile.id === values.salesProfileId);

  const costIso = values.costCurrency?.iso;
  const billingIso = values.billingCurrency?.iso;
  const decimalFormat = decimalInputFormat(i18n.language);

  const validationBody = useMemo((): (AllocationBody & { id?: number }) | null => {
    if (readOnly) return null;
    const professional = values.professional;
    const project = values.project;
    if (!professional?.id || !project?.id || !values.startDate) return null;
    return {
      id: target?.allocation.id,
      professionalId: professional.id,
      projectId: project.id,
      allocatedPercentage: values.allocatedPercentage ?? 0,
      billable: values.billable ?? true,
      startDate: toApiDate(values.startDate) as string,
      endDate: toApiDate(values.endDate ?? null),
      costShareBr: values.costShareBr ?? 0,
      costShareUs: values.costShareUs ?? 0,
      notes: values.notes || null,
      fimConfirmado: values.fimConfirmado ?? false,
      tagIds: values.tagIds ?? [],
      ...noticeBody(values, project as ProjectOption),
      ...valuesBody(values),
    };
  }, [values, target, readOnly]);

  const { data: validation } = useValidateAllocation(validationBody);
  const blockers = validation?.blockers ?? [];
  const warnings = submitWarnings.length > 0 ? submitWarnings : (validation?.warnings ?? []);

  const serverErrorFor = useCallback(
    (apiField: string): string | undefined => {
      const blocker = blockers.find((b) => b.fields?.includes(apiField));
      return blocker ? t(i18nKeyForError(blocker.code) as never) : undefined;
    },
    [blockers, t],
  );

  const selectionError = useCallback(
    (message: string | undefined): string | undefined => {
      if (!message) return undefined;
      return message === 'required' ? t('common:errors.required') : message;
    },
    [t],
  );

  const professionalError =
    serverErrorFor('professionalId') ?? selectionError(errors.professional?.message);
  const projectError = serverErrorFor('projectId') ?? selectionError(errors.project?.message);
  const contractError = errors.contract?.message ? t('common:errors.required') : undefined;

  const valueError = useCallback(
    (field: 'monthlyCost' | 'hourlyRate' | 'soldHours') =>
      serverErrorFor(field) ??
      (errors[field]?.message ? t('alocacao:erros.invalidFinancialValue') : undefined),
    [serverErrorFor, errors, t],
  );

  const onSubmit = handleSubmit((formValues) => {
    setSubmitWarnings([]);
    const body: AllocationBody = {
      professionalId: formValues.professional.id,
      projectId: formValues.project.id,
      allocatedPercentage: formValues.allocatedPercentage,
      billable: formValues.billable,
      startDate: toApiDate(formValues.startDate) as string,
      endDate: toApiDate(formValues.endDate),
      costShareBr: formValues.costShareBr,
      costShareUs: formValues.costShareUs,
      notes: formValues.notes || null,
      confirmWarnings: warnings.length > 0,
      fimConfirmado: formValues.fimConfirmado,
      tagIds: formValues.tagIds,
      ...noticeBody(formValues, formValues.project),
      ...valuesBody(formValues),
    };
    saveMutation.mutate(
      { id: target?.allocation.id, body },
      {
        onSuccess: () => {
          toast.success({
            title: t(target ? 'alocacao:toasts.atualizarSucesso' : 'alocacao:toasts.criarSucesso'),
          });
          onSaveSuccess();
        },
        onError: (error) => {
          const apiError = parseApiError(error);
          if (!apiError) {
            toast.danger({ title: t('alocacao:toasts.erro') });
            return;
          }
          if (apiError.code === 'UNCONFIRMED_WARNINGS') {
            setSubmitWarnings(apiError.warnings ?? []);
            return;
          }
          if (apiError.statusCode === 422 && apiError.fields) {
            for (const apiField of apiError.fields) {
              const field = formFieldFor(apiField);
              if (field) setError(field, { message: t(i18nKeyForError(apiError.code) as never) });
            }
            return;
          }
          toast.danger({ title: t(i18nKeyForError(apiError.code) as never) });
        },
      },
    );
  });

  const canSave = isValid && blockers.length === 0 && !saveMutation.isPending;

  // Repassado ao SearchCombobox que chama no topo do próprio render — um
  // único useQuery por render, só o contractId varia.
  const useProjectsOfContract = useCallback(
    // biome-ignore lint/correctness/useHookAtTopLevel: ver comentário acima
    (search: string) => useProjects(search, selectedContractId),
    [selectedContractId],
  );

  // Falha silenciosa: sem contrato ativo ou fora do escopo, a pré-validação já bloqueia.
  const applyProfessionalDefaults = useCallback(
    async (professionalId: number) => {
      try {
        const defaults = await fetchDefaults({ professionalId });
        if (getValues('professional')?.id !== professionalId) return;
        const currency = defaults.costCurrency;
        setValue('costCurrency', currency ? { id: currency.id, iso: currency.iso } : null);
        setValue('monthlyCost', defaults.monthlyCost, { shouldValidate: true });
        setValue('costShareBr', defaults.costShareBr, { shouldValidate: true });
        setValue('costShareUs', defaults.costShareUs, { shouldValidate: true });
      } catch {
        /* intencional */
      }
    },
    [fetchDefaults, getValues, setValue],
  );

  const applyNotice = useCallback(() => {
    if (!getValues('notified')) {
      setValue('noticeDate', null, { shouldValidate: true });
      setValue('noticeDays', 0, { shouldValidate: true });
      return;
    }
    const noticeDate = getValues('noticeDate');
    if (noticeDate) {
      setValue('endDate', noticeEndDate(noticeDate, getValues('noticeDays') ?? 0), {
        shouldValidate: true,
      });
    }
  }, [getValues, setValue]);

  const applyContract = useCallback(
    (contract: SelectedOption | null, previous: SelectedOption | undefined) => {
      if (contract?.id === previous?.id) return;
      setValue('project', null as unknown as ProjectOption, { shouldValidate: true });
      setValue('salesProfileId', null);
      setValue('billingCurrency', null);
      setValue('hourlyRate', 0, { shouldValidate: true });
      setValue('soldHours', 0, { shouldValidate: true });
    },
    [setValue],
  );

  const applyProjectRules = useCallback(
    (project: ProjectOption) => {
      if (!projectBills(project)) {
        setValue('billable', false);
        setValue('hourlyRate', 0, { shouldValidate: true });
        setValue('soldHours', 0, { shouldValidate: true });
      }
      if (!project.isPool) {
        setValue('notified', false);
        applyNotice();
      }
    },
    [setValue, applyNotice],
  );

  const applySalesProfile = useCallback(
    (profile: SalesProfileOption | null) => {
      const hourlyRate = profile && billsProject ? profile.hourlyRate : 0;
      setValue('salesProfileId', profile?.id ?? null, { shouldValidate: true });
      setValue('billingCurrency', profile?.currency ?? null);
      setValue('hourlyRate', hourlyRate, { shouldValidate: true });
      setValue('soldHours', hourlyRate > 0 ? DEFAULT_SOLD_HOURS : 0, { shouldValidate: true });
    },
    [billsProject, setValue],
  );

  return {
    control,
    register,
    getValues,
    errors,
    onSubmit,
    isDirty,
    target,
    allocation: target?.allocation as Allocation | undefined,
    billsProject,
    isPool,
    selectedContractId,
    selectedProject,
    costIso,
    billingIso,
    decimalFormat,
    salesProfiles,
    selectedProfile,
    monthlyCost: values.monthlyCost ?? null,
    allocatedPercentage: values.allocatedPercentage ?? 0,
    hourlyRate: values.hourlyRate ?? 0,
    soldHours: values.soldHours ?? 0,
    notified: !!values.notified,
    professionalError,
    projectError,
    contractError,
    serverErrorFor,
    valueError,
    blockers,
    warnings,
    canSave,
    isSaving: saveMutation.isPending,
    useAllocatableContracts,
    useProjectsOfContract,
    selectedTags,
    setSelectedTags,
    applyProfessionalDefaults,
    applyContract,
    applyProjectRules,
    applySalesProfile,
    applyNotice,
  };
}

export type AllocationFormViewModel = ReturnType<typeof useAllocationForm>;
